import React from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import DadCampaignModal from './DadCampaignModal';
import DadImportModal from './DadImportModal';
import { getDadCampaignDisplayLabel } from './memberConstants';
import WebinarImportModal from './WebinarImportModal';

const MembersOutreachSection = ({ joinFirstMode, workflow }) => {
  const campaignProgressDetail = joinFirstMode
    ? 'Active dad outreach and follow-up campaigns with the latest import history.'
    : 'Active webinar and follow-up campaigns with the latest import history.';
  const recentImportsTitle = joinFirstMode ? 'Recent Attendance Imports' : 'Recent Webinar Imports';
  const recentImportsEmpty = joinFirstMode ? 'No attendance imports yet.' : 'No webinar imports yet.';
  const outreachPanelTitle = joinFirstMode ? 'Dad Pipeline Outreach' : 'Dad Webinar Outreach';
  const outreachPanelDetail = joinFirstMode
    ? 'Select dads here, then choose the next outreach email to move them toward the right next step.'
    : 'Select dads here, then choose which webinar sequence email to send.';
  const visibleInviteLabel = joinFirstMode ? 'visible invite-stage dads' : 'visible webinar invited';
  const selectInviteLabel = joinFirstMode ? 'Select Visible Invite-Stage' : 'Select Visible Webinar Invited';
  const sendSequenceLabel = joinFirstMode ? 'Send Next-Step Outreach' : 'Send Webinar Invite Sequence';
  const sequenceModalTitle = joinFirstMode ? 'Send Next-Step Outreach' : 'Send Webinar Invite Sequence';
  const sequenceModalDetail = joinFirstMode
    ? 'Choose the next email in the sequence, review the recipients, and decide whether to send now or stagger delivery.'
    : 'Choose the webinar email step, review the recipients, and decide whether to send now or stagger delivery.';
  const previewHelperText = joinFirstMode
    ? 'The sent email uses the current dads outreach sequence so you can move high-intent dads toward the right next step.'
    : 'The sent email uses the Dad Partners webinar email template with a button to register and a smaller link to learn about the Dad Partners Founding Forty.';

  return (
    <>
      <Card className="border border-gray-200 bg-white p-4 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Dad Campaign Progress</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {campaignProgressDetail}
            </p>
          </div>
          {workflow.dashboardLoading && <Loader2 className="h-5 w-5 animate-spin text-aqua" />}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Active Campaigns</h4>
            <div className="mt-3 space-y-3">
              {(workflow.dadCampaignDashboard.active_campaigns || []).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No active dad campaigns right now.</p>
              ) : (
                workflow.dadCampaignDashboard.active_campaigns.slice(0, 3).map((campaign) => (
                  <div key={campaign.campaign_id} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-[#2a2a32] dark:bg-[#0f0f13]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{campaign.sequence_name_snapshot || campaign.sequence_name || 'Dad campaign'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {campaign.step_name_snapshot || campaign.step_name || 'Email'} · {getDadCampaignDisplayLabel(campaign)}
                        </p>
                      </div>
                      <Badge className="border border-gray-200 bg-gray-100 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
                        {campaign.status || 'scheduled'}
                      </Badge>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <div>
                        <span className="block text-gray-400 dark:text-gray-500">Sent</span>
                        <span className="text-gray-900 dark:text-white">{campaign.sent_count || 0}</span>
                      </div>
                      <div>
                        <span className="block text-gray-400 dark:text-gray-500">Remaining</span>
                        <span className="text-gray-900 dark:text-white">{campaign.remaining_count || 0}</span>
                      </div>
                      <div>
                        <span className="block text-gray-400 dark:text-gray-500">Next Run</span>
                        <span className="text-gray-900 dark:text-white">
                          {campaign.next_run_at ? new Date(campaign.next_run_at).toLocaleString() : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{recentImportsTitle}</h4>
            <div className="mt-3 space-y-3">
              {(workflow.dadCampaignDashboard.recent_imports || []).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{recentImportsEmpty}</p>
              ) : (
                workflow.dadCampaignDashboard.recent_imports.slice(0, 3).map((importRun) => (
                  <div key={importRun.webinar_import_key} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-[#2a2a32] dark:bg-[#0f0f13]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {importRun.webinar_label || importRun.webinar_import_key || 'Webinar import'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {importRun.created_count || 0} created · {importRun.updated_count || 0} updated
                        </p>
                      </div>
                      <Badge className="border border-gray-200 bg-gray-100 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
                        {importRun.attended_count || 0} attended / {importRun.no_show_count || 0} missed
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border border-gray-200 bg-white p-4 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{outreachPanelTitle}</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {outreachPanelDetail}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className="border border-gray-200 bg-gray-100 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
                {workflow.selectedDadIds.length} selected
              </Badge>
              <Badge className="border border-gray-200 bg-gray-100 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
                {workflow.visibleProspectIds.length} visible prospects
              </Badge>
              <Badge className="border border-gray-200 bg-gray-100 text-gray-700 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
                {workflow.visibleWebinarInvitedIds.length} {visibleInviteLabel}
              </Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => workflow.toggleGroupSelection(workflow.visibleProspectIds)}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
            >
              Select Visible Prospects
            </Button>
            <Button
              variant="outline"
              onClick={() => workflow.toggleGroupSelection(workflow.visibleWebinarInvitedIds)}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
            >
              {selectInviteLabel}
            </Button>
            <Button
              variant="outline"
              onClick={workflow.clearSelection}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
            >
              Clear Selection
            </Button>
            <Button
              onClick={workflow.openSequenceModal}
              className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark"
            >
              <Mail className="h-4 w-4" />
              {sendSequenceLabel}
            </Button>
          </div>
        </div>
      </Card>

      <DadImportModal
        open={workflow.showImportModal}
        onClose={workflow.closeDadImportModal}
        importDefaults={workflow.importDefaults}
        setImportDefaults={workflow.setImportDefaults}
        importCsv={workflow.importCsv}
        setImportCsv={workflow.setImportCsv}
        onFileChange={workflow.handleImportFile}
        onSubmit={workflow.submitDadImport}
        importingDads={workflow.importingDads}
      />

      <WebinarImportModal
        open={workflow.showWebinarImportModal}
        onClose={workflow.closeWebinarImportModal}
        webinarImportKey={workflow.webinarImportKey}
        setWebinarImportKey={workflow.setWebinarImportKey}
        webinarImportCsv={workflow.webinarImportCsv}
        setWebinarImportCsv={workflow.setWebinarImportCsv}
        onFileChange={workflow.handleWebinarImportFile}
        onSubmit={workflow.submitWebinarImport}
        webinarImporting={workflow.webinarImporting}
      />

      <DadCampaignModal
        open={workflow.showSequenceModal}
        onClose={workflow.closeSequenceModal}
        title={sequenceModalTitle}
        detail={sequenceModalDetail}
        previewHelperText={previewHelperText}
        sequences={workflow.sequences}
        campaignForm={workflow.campaignForm}
        selectedSequenceSteps={workflow.selectedSequenceSteps}
        selectedStep={workflow.selectedStep}
        remainingSequenceSteps={workflow.remainingSequenceSteps}
        campaignPreview={workflow.campaignPreview}
        previewingCampaign={workflow.previewingCampaign}
        loadingSequences={workflow.loadingSequences}
        campaignSubmitting={workflow.campaignSubmitting}
        onCampaignFormChange={workflow.handleCampaignFormChange}
        onChainDelayChange={workflow.handleChainDelayChange}
        onSubmit={workflow.handleCreateCampaign}
      />
    </>
  );
};

export default MembersOutreachSection;
